import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, dashboardPath } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const PW_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^(?:\+?91[\-\s]?)?[6-9]\d{9}$/;   // Indian mobile (10 digits, starting with 6/7/8/9), optional +91 prefix

const ROLES = [
  { key: "school_admin", label: "School" },
  { key: "parent", label: "Parent" },
  { key: "vendor", label: "Vendor" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("school_admin");
  const [f, setF] = useState({ name: "", email: "", password: "", school_name: "", company_name: "", student_class: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    // Client-side gate — server will re-validate.
    const email = f.email.trim();
    const phoneNorm = f.phone.replace(/[\s\-]/g, "");
    if (!EMAIL_REGEX.test(email)) {
      toast.error("Please enter a valid email address."); return;
    }
    if (!MOBILE_REGEX.test(phoneNorm)) {
      toast.error("Please enter a valid 10-digit Indian mobile number."); return;
    }
    if (!PW_REGEX.test(f.password)) {
      toast.error("Password must be 12+ chars with uppercase, lowercase, digit and special."); return;
    }
    setLoading(true);
    try {
      const payload = { name: f.name, email, password: f.password, role, phone: phoneNorm };
      if (role === "school_admin") payload.school_name = f.school_name;
      if (role === "vendor") payload.company_name = f.company_name;
      if (role === "parent") payload.student_class = f.student_class;
      const u = await register(payload);
      toast.success("Account created!");
      nav(dashboardPath(u.role));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" aria-label="SchoolLane – Home" className="inline-block mb-8">
          <Logo height={56} />
        </Link>
        <div className="sl-card p-8">
          <h1 className="text-2xl font-heading font-bold text-slate-900">Create your account</h1>
          <p className="text-slate-600 mt-1 text-sm">Choose your role to get started.</p>

          <div className="mt-6 grid grid-cols-3 gap-2 p-1 bg-slate-100 rounded-md">
            {ROLES.map((r) => (
              <button key={r.key} type="button" data-testid={`role-${r.key}`}
                onClick={() => setRole(r.key)}
                className={`py-2 text-sm font-semibold rounded transition-colors duration-200 ${role === r.key ? "bg-white text-brand-blue shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
                {r.label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 grid md:grid-cols-2 gap-4">
            <input required data-testid="reg-name" placeholder="Full name" value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className="rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            <input required data-testid="reg-email" type="email" placeholder="Email address *" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} className="rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            <div className="md:col-span-2">
              <input required data-testid="reg-password" type="password" placeholder="Password (12+ chars, mixed case, digit, special)" value={f.password} onChange={(e) => setF({...f, password: e.target.value})} className="w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
              {f.password && !PW_REGEX.test(f.password) && (
                <div className="mt-1 text-xs text-red-600" data-testid="pw-hint">Password must be at least 12 characters and include uppercase, lowercase, a number and a special character.</div>
              )}
            </div>
            <div className="md:col-span-2">
              <input required data-testid="reg-phone" type="tel" inputMode="numeric" placeholder="Mobile number *  (e.g. 9876543210)"
                     value={f.phone}
                     onChange={(e) => setF({...f, phone: e.target.value})}
                     className="w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
              {f.phone && !MOBILE_REGEX.test(f.phone.replace(/[\s\-]/g, "")) && (
                <div className="mt-1 text-xs text-red-600" data-testid="phone-hint">
                  Enter a valid 10-digit Indian mobile number (starts with 6, 7, 8 or 9). +91 prefix optional.
                </div>
              )}
            </div>
            {role === "school_admin" && (
              <input required data-testid="reg-school-name" placeholder="School name" value={f.school_name} onChange={(e) => setF({...f, school_name: e.target.value})} className="md:col-span-2 rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            )}
            {role === "vendor" && (
              <input required data-testid="reg-company" placeholder="Company name" value={f.company_name} onChange={(e) => setF({...f, company_name: e.target.value})} className="md:col-span-2 rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            )}
            {role === "parent" && (
              <input required data-testid="reg-class" placeholder="Child's class (e.g., 8)" value={f.student_class} onChange={(e) => setF({...f, student_class: e.target.value})} className="md:col-span-2 rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            )}
            <button disabled={loading} data-testid="reg-submit" className="md:col-span-2 rounded-md bg-brand-blue text-white font-semibold py-3 hover:bg-brand-blueDark transition-colors duration-200 disabled:opacity-60">
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>
          <div className="mt-4 text-sm text-slate-600">
            Already have an account? <Link to="/login" className="text-brand-blue font-semibold">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
